import { configureStore, isAnyOf } from "@reduxjs/toolkit";

// ── Existing non-auth reducers (kept exactly as-is) ───────────────────────────
import { PostsReducer, toggleMenu } from "./reducers/PostsReducer";
import todoReducers from "./reducers/Reducers";

// ── New RTK auth layer ────────────────────────────────────────────────────────
import authReducer, { logout } from "./authSlice";
import equipmentReducer from "./equipmentSlice";
import projectReducer, { setActiveProject, clearActiveProject } from "./projectSlice";
import { authApi } from "./authApi";
import { appApi } from "./appApi";

// appApi caches many endpoints purely by their arguments (e.g. getMyCalendar by
// "month" alone, with no user id in the key), so switching accounts in the same
// browser session/tab without clearing the cache would serve the previous
// user's data until a query's args happen to change. Wipe the whole appApi
// cache on every login/logout so no data ever leaks between sessions.
const resetApiCacheOnAuthChange = (storeApi) => (next) => (action) => {
  const result = next(action);
  // Switching the active project also wipes it: lists like equipment and
  // vehicles are scoped to the x-project-id header, but their cache key is just
  // the query args, so without this the previous project's rows stayed on
  // screen until a hard reload.
  if (
    isAnyOf(
      logout,
      authApi.endpoints.login.matchFulfilled,
      setActiveProject,
      clearActiveProject,
    )(action)
  ) {
    storeApi.dispatch(appApi.util.resetApiState());
  }
  return result;
};

export const store = configureStore({
  reducer: {
    // Preserved from old store — no behaviour change
    sideMenu:     toggleMenu,
    posts:        PostsReducer,
    todoReducers,

    // Auth state (token, user, isAuthenticated) — replaces AuthReducer
    auth:         authReducer,

    // Feature slices
    equipment:    equipmentReducer,
    project:      projectReducer,

    // RTK Query cache slices
    [authApi.reducerPath]: authApi.reducer,
    [appApi.reducerPath]:  appApi.reducer,
  },

  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      authApi.middleware,   // enables RTK Query caching/invalidation for auth
      appApi.middleware,    // enables RTK Query caching/invalidation for app data
      resetApiCacheOnAuthChange,
    ),
});
